'use client'

import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { trackAdSlotState } from '@/lib/tracking'
import type { AdSlotState } from '@/lib/funnel-property-enums'
import Sentry from '@/lib/sentry-safe'
import { useCookieConsent } from './cookie-consent'

// AdSense configuration - set your publisher ID in .env
const ADSENSE_CLIENT = process.env.NEXT_PUBLIC_ADSENSE_CLIENT || ''

/**
 * adsbygoogle.push({}) does not target the <ins> the caller rendered — it fills
 * the FIRST unprocessed `ins.adsbygoogle` in document order. When that element
 * measures zero width, AdSense throws
 * `TagError: No slot size for availableWidth=0` and the impression is lost.
 *
 * Two zero-width elements reach that position in production: the auto-ads
 * anchor slot Google injects itself (observed as a 0x0 `ins.adsbygoogle` with
 * no data-ad-slot), and a slot mid-teardown during a client-side navigation.
 * Verified 2026-08-26 (TES-1876): planting a 0-width `<ins>` ahead of the
 * homepage slot leaves the homepage slot at data-adsbygoogle-status=null with
 * no iframe — it never fills.
 */
const MIN_SLOT_WIDTH_PX = 1

/**
 * How long to wait, from the first attempt that is blocked, before forcing the
 * issue — either pushing into our own still-zero-width slot, or spending a push
 * to clear a foreign zero-width slot that is sitting in front of it.
 */
const BLOCKED_WAIT_TIMEOUT_MS = 10_000

/**
 * How long a slot may sit unanswered before its band is allowed to collapse.
 * The collapse itself waits for the band to leave the viewport (see AdBand).
 */
const UNANSWERED_TIMEOUT_MS = 3_000

/** Where `scrollend` is missing, a pending reopen re-checks this long after a scroll. */
const SCROLL_CHECK_MS = 200

const ADSENSE_SCRIPT_SELECTOR = 'script[src*="pagead2.googlesyndication.com"]'

/** Phones get one fixed 320x100 unit, so reserved height equals rendered height. */
const SM_BREAKPOINT = 640
const PHONE_QUERY = `(max-width: ${SM_BREAKPOINT - 1}px)`

function subscribePhone(onChange: () => void) {
  if (typeof window.matchMedia !== 'function') return () => {}
  const query = window.matchMedia(PHONE_QUERY)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function readIsPhone() {
  return typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches
}

function useIsPhone() {
  return useSyncExternalStore(subscribePhone, readIsPhone, () => false)
}

/**
 * Poll cadence, and how many polls to keep taking *after* the AdSense global
 * exists. The wait for the global itself is deliberately uncapped: AdSenseLoader
 * only injects the script on a real interaction and has no time fallback
 * (TES-858/TES-840), so a user who reads for a minute before their first tap
 * would otherwise find the slot already given up on. Until then each poll is a
 * property read, and the effect cleanup stops it on unmount.
 */
const POLL_INTERVAL_MS = 250
const POLL_MAX_ATTEMPTS = 240 // ~60s after the script lands

/**
 * Pushes spent clearing zero-width slots that sit ahead of ours. `push()`
 * resolves normally even when the slot it claimed is unusable — the TagError is
 * raised asynchronously inside adsbygoogle.js, so there is nothing to catch —
 * but the claim itself is real: AdSense stamps `data-adsbygoogle-status` on a
 * slot it failed. Spending a push therefore clears the blocker and moves ours
 * to the front. Kept small because each one also consumes a slot.
 */
const MAX_CLEARING_PUSHES = 2

/** The element adsbygoogle.push({}) will actually fill, if any. */
function nextUnfilledSlot(): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>('ins.adsbygoogle')
  for (const node of nodes) {
    if (!node.getAttribute('data-adsbygoogle-status')) return node
  }
  return null
}

interface AdPlacementProps {
  slot: string
  /**
   * The shape asked of AdSense. A request, not a guarantee: on a responsive
   * unit it binds at `sm` and up and is advisory below that, where
   * `data-full-width-responsive` outranks it (TES-2325). Read `reservedShape`
   * for the shape the layout actually holds space for.
   */
  format?: 'auto' | 'horizontal' | 'vertical' | 'rectangle'
  className?: string
  style?: React.CSSProperties
  responsive?: boolean
  /** Use intersection observer to lazy-load below-fold ads */
  lazy?: boolean
}

/**
 * AdSense ad placement component
 *
 * Fixes over original implementation:
 * - hasPushedRef prevents multiple adsbygoogle.push({}) race conditions
 * - Intersection observer for lazy loading below-fold ads
 * - Error handling for script load failures
 * - min-height CSS reserves space to prevent CLS
 */
function AdPlacement({
  slot,
  format = 'auto',
  className = '',
  style,
  responsive = true,
  lazy = false
}: AdPlacementProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const insRef = useRef<HTMLModElement>(null)
  const hasPushedRef = useRef(false)
  const [isVisible, setIsVisible] = useState(!lazy)
  const [adError, setAdError] = useState(false)
  const phoneUnit = useIsPhone() && format === 'horizontal'
  const [pushed, setPushed] = useState(false)
  const { marketingAllowed } = useCookieConsent()

  // Intersection observer for lazy loading below-fold ads
  // eslint-disable-next-line react-doctor/no-cascading-set-state -- setIsVisible here triggers the ad-push effect; two-phase lazy-load pattern is intentional
  useEffect(() => {
    if (!lazy || isVisible) return

    const el = containerRef.current
    if (!el) {
      // Retry after a short delay if element not yet mounted
      const retryTimer = setTimeout(() => {
        const retryEl = containerRef.current
        if (retryEl && !isVisible) {
          setIsVisible(true)
        }
      }, 100)
      return () => clearTimeout(retryTimer)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [lazy, isVisible])

  // Push ad once visible, laid out with a real width, and the AdSense script is
  // loaded (by AdSenseLoader).
  // eslint-disable-next-line react-doctor/no-cascading-set-state -- setAdError here is an error path only; not a render-triggered cascade
  useEffect(() => {
    if (!isVisible || !marketingAllowed || !ADSENSE_CLIENT || !slot || hasPushedRef.current) {
      return
    }

    let disposed = false
    let attempts = 0
    let forcing = false
    let clearingPushes = 0
    let resizeObserver: ResizeObserver | null = null
    let poll: ReturnType<typeof setInterval> | null = null
    let blockedDeadline: ReturnType<typeof setTimeout> | null = null
    let script: HTMLScriptElement | null = null

    const teardown = () => {
      resizeObserver?.disconnect()
      resizeObserver = null
      if (poll) clearInterval(poll)
      poll = null
      if (blockedDeadline) clearTimeout(blockedDeadline)
      blockedDeadline = null
      script?.removeEventListener('load', tryPush)
      script?.removeEventListener('error', onScriptError)
      script = null
    }

    function onScriptError() {
      if (disposed) return
      // Ad blockers are the overwhelming cause of a failed AdSense script load.
      // That is a user-environment condition, not a defect worth an event.
      setAdError(true)
      teardown()
    }

    /**
     * Arm the give-up timer on the first *blocked* attempt rather than on
     * mount. AdSenseLoader gates the script behind a real interaction, so it
     * routinely lands several seconds after the slot becomes visible; a timer
     * started at mount would expire while we were still waiting on the script.
     */
    function armDeadline() {
      blockedDeadline ??= setTimeout(() => {
        forcing = true
        tryPush()
      }, BLOCKED_WAIT_TIMEOUT_MS)
    }

    function push() {
      try {
        window.adsbygoogle.push({})
        return true
      } catch (error) {
        setAdError(true)
        teardown()
        Sentry.captureException(error, {
          tags: { context: 'AdPlacement', action: 'pushAd', slot }
        })
        return false
      }
    }

    function tryPush() {
      if (disposed || hasPushedRef.current || !window.adsbygoogle) return

      const el = insRef.current
      if (!el?.isConnected) return

      // The only trustworthy signal that a push landed on our element. push()
      // itself tells us nothing: it resolves normally even when the slot it
      // claimed was unusable, because the TagError comes from adsbygoogle.js
      // asynchronously. Verified on production 2026-08-26 (TES-1876).
      if (el.getAttribute('data-adsbygoogle-status')) {
        hasPushedRef.current = true
        teardown()
        return
      }

      const target = nextUnfilledSlot()

      if (target === el) {
        // Ours is next in line. Hold while it is still collapsed, since a
        // zero-width claim is what produces `No slot size for availableWidth=0`.
        if (!forcing && el.getBoundingClientRect().width < MIN_SLOT_WIDTH_PX) {
          armDeadline()
          return
        }
        if (push()) {
          hasPushedRef.current = true
          setPushed(true)
        }
        teardown()
        return
      }

      // Something else is ahead of us and would absorb this push.
      if (!target || target.getBoundingClientRect().width >= MIN_SLOT_WIDTH_PX) {
        // A real slot — another placement's, and its own effect will claim it.
        // Nothing to do but let the poll pick us up once it has.
        return
      }

      // A zero-width slot ahead of ours: Google's auto-ads anchor, or one
      // mid-teardown during a client-side navigation. It usually resolves on
      // its own within a second or two, so wait first — see MAX_CLEARING_PUSHES
      // for why spending a push is the fallback rather than the first move.
      if (!forcing) {
        armDeadline()
        return
      }
      if (clearingPushes >= MAX_CLEARING_PUSHES) {
        teardown()
        return
      }
      clearingPushes++
      push()
      // Deliberately not marking ourselves pushed: that push went to the
      // blocker. The next poll re-checks who is at the front now.
    }

    // Re-check as soon as our slot gains a width (mount, transition, reflow).
    if (insRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => tryPush())
      resizeObserver.observe(insRef.current)
    }

    const watchScript = () => {
      if (script) return
      script = document.querySelector<HTMLScriptElement>(
        'script[src*="pagead2.googlesyndication.com"]'
      )
      script?.addEventListener('load', tryPush)
      script?.addEventListener('error', onScriptError)
    }

    // Covers the AdSense script arriving late and an earlier zero-width slot
    // being consumed by someone else.
    poll = setInterval(() => {
      watchScript()
      // Don't spend the attempt budget waiting on an interaction-gated script.
      if (!window.adsbygoogle) return
      if (++attempts >= POLL_MAX_ATTEMPTS) {
        teardown()
        return
      }
      tryPush()
    }, POLL_INTERVAL_MS)

    watchScript()

    tryPush()

    return () => {
      disposed = true
      teardown()
    }
  }, [isVisible, marketingAllowed, slot])

  // Don't render if no consent or not configured
  if (!marketingAllowed || !ADSENSE_CLIENT) {
    return null
  }

  // Sizes are only the pre-fill placeholder: any unit carrying `data-ad-format`
  // is a responsive unit and AdSense rewrites width/height on fill. Pinning a
  // height on the responsive shapes just guarantees the rewrite is a jump.
  const formatStyles: Record<string, React.CSSProperties> = {
    auto: { display: 'block' },
    horizontal: { display: 'block' },
    vertical: { display: 'block', width: '160px', height: '600px' },
    rectangle: { display: 'block', width: '300px', height: '250px' }
  }

  /**
   * The reservation is keyed on `band` (one placement, both of its shapes), not
   * on the requested "horizontal", which only means something on
   * `data-ad-format`.
   */
  const reservedShape = format === 'horizontal' ? 'band' : format

  return (
    <div
      ref={containerRef}
      className={`ad-container ${className}`}
      // Drives the pre-fill reservation in globals.css (TES-1965, TES-2325).
      data-ad-shape={reservedShape}
      // Set once push() has run. AdBand starts its unanswered clock from here,
      // never from mount: the script is interaction-gated and a clock started
      // earlier would collapse a slot that has not been offered to AdSense yet.
      data-ad-pushed={pushed ? 'true' : undefined}
      // width is explicit so the slot can never measure zero inside a flex or
      // grid ancestor that would otherwise shrink it to its content (TES-1876).
      style={{ width: '100%', ...style }}
    >
      {isVisible && !adError && (
        <ins
          ref={insRef}
          className={phoneUnit ? 'adsbygoogle tls-ad-phone' : 'adsbygoogle'}
          style={phoneUnit ? undefined : { ...formatStyles[format], ...(responsive && { width: '100%' }) }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={slot}
          // Phones omit the format hint and the full-width flag: either one
          // makes AdSense serve a viewport-wide square into a box that was
          // reserved at 100px. Desktop keeps the horizontal request, which
          // binds there (1280px serves 942x90 into the 90px reserved).
          data-ad-format={phoneUnit ? undefined : format}
          data-full-width-responsive={phoneUnit ? undefined : responsive ? 'true' : 'false'}
        />
      )}
    </div>
  )
}

/**
 * §08.1's quarantine, as one class list: 1px hairline frame, quarantine ground,
 * SQUARE corners. The one surface the no-card rule exempts is a frame, not a
 * rounded card, and §08.1 grants it to every slot — the placement map varies
 * *where* an ad may appear, never whether it is quarantined once it does.
 *
 * Framing is deliberately breakpoint-split (TES-1965). `data-full-width-
 * responsive` lets AdSense widen a mobile creative to the viewport and shift it
 * back with a negative margin, so below `sm` the creative is always edge-to-edge
 * no matter how much padding sits above it. A bordered, inset card there gets
 * sliced through both side borders. Measured again for the in-feed slot on
 * production at 375px (logs/tes-2328/before-prod/report.json): the container is
 * 343px and the creative is 375px, so it overhangs by exactly the page gutter.
 * So the band goes full-bleed at mobile and is closed with hairlines top and
 * bottom; the side hairlines return at `sm`, where the creative stays inside the
 * container.
 *
 * Whichever component carries this owns the *only* frame its slot gets —
 * callers pass spacing, not chrome.
 */
const QUARANTINE_BAND =
  'tls-ad-band rounded-[var(--ls-radius-0)] border-y border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] py-4 max-sm:mx-[calc(50%-50vw)] sm:border-x sm:px-4'

/**
 * The quarantine frame around one slot, plus the rule for giving its space back.
 *
 * Reserved height equals rendered height (100px on a phone, 90px from `sm`), so a
 * filled ad shifts nothing. A slot that gets no ad (`unfilled`,
 * `unfill-optimized`, no answer 3s after the push, or a blocked script: one
 * that errored, or loaded without defining the AdSense library) collapses, but
 * only while
 * the band is entirely BELOW the viewport: collapsing on screen, or above it, would
 * jolt whatever the reader is looking at. If it is on screen the reserved box stays until it
 * scrolls out. A slow script is never treated as blocked. A late fill reopens a
 * collapsed band the next time the band sits entirely below the viewport.
 *
 * The same observer reports `ad_slot_state` once per mount, on the first
 * intersection with the viewport and not on mount: lazy slots mount without
 * being scrolled to, so a mount-time event would count slots nobody saw. The
 * state is AdSense's answer at that point or, if it answers while the band is
 * still on screen, the moment it does. `unresolved` is no answer within
 * UNANSWERED_TIMEOUT_MS of the push. A fill after that is not reported again.
 */
function AdBand({
  className,
  caption,
  captionClassName,
  children
}: {
  className: string
  caption: string
  captionClassName: string
  children: React.ReactNode
}) {
  const bandRef = useRef<HTMLDivElement>(null)
  const [collapsed, setCollapsed] = useState(false)

  // eslint-disable-next-line react-doctor/no-cascading-set-state -- collapse is driven by DOM attributes AdSense writes, which React is never told about
  useEffect(() => {
    const band = bandRef.current
    if (!band || typeof IntersectionObserver === 'undefined') return

    // Only a band that sits entirely below the viewport may change height:
    // above it, engines without scroll anchoring (iOS WebKit) jump the page by
    // the band's height. A collapsed band keeps its place in the flow at zero
    // height, so its position can still be read here.
    const belowViewport = () => band.getBoundingClientRect().top >= window.innerHeight

    let inView = false
    let unanswered = false
    let blocked = false
    let everInView = false
    let reported = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let script: HTMLScriptElement | null = null
    let isCollapsed = false
    let scrollCheck: ReturnType<typeof setTimeout> | null = null
    let listening = false
    const scrollEvent = 'onscrollend' in window ? 'scrollend' : 'scroll'

    const collapse = (next: boolean) => {
      isCollapsed = next
      setCollapsed(next)
    }

    // A jump from below the band straight to the top (scrollTo(0, 0), a hash
    // link, a nav tap) carries a zero-height band from above the viewport to
    // below it without intersecting, so the observer never fires. While a
    // fill is pending, the end of each scroll re-checks instead.
    const onScroll = () => {
      if (scrollEvent === 'scrollend') {
        settle()
        return
      }
      if (scrollCheck) return
      scrollCheck = setTimeout(() => {
        scrollCheck = null
        settle()
      }, SCROLL_CHECK_MS)
    }

    const listenForScroll = (pending: boolean) => {
      if (pending === listening) return
      listening = pending
      if (pending) window.addEventListener(scrollEvent, onScroll, { passive: true })
      else window.removeEventListener(scrollEvent, onScroll)
    }

    const reportOnce = () => {
      if (reported || !everInView) return
      const ins = band.querySelector('ins.adsbygoogle')
      const status = ins?.getAttribute('data-ad-status')
      let state: AdSlotState | null = null
      if (status === 'filled') state = 'filled'
      else if (status === 'unfilled' || status === 'unfill-optimized') state = 'unfilled'
      else if (unanswered) state = 'unresolved'
      const adSlot = ins?.getAttribute('data-ad-slot')
      if (!state || !adSlot) return
      reported = true
      trackAdSlotState({ state, ad_slot: adSlot })
    }

    const settle = () => {
      reportOnce()
      const status = band.querySelector('ins.adsbygoogle')?.getAttribute('data-ad-status')
      // A late fill waits until the band is entirely below the viewport, then
      // reopens: on screen it would jump the page, and above it iOS WebKit
      // (no scroll anchoring) shifts too. The status stays 'filled', so every
      // later intersection change or scroll end re-checks; that is the
      // pending reopen.
      if (status === 'filled') {
        if (belowViewport()) collapse(false)
        listenForScroll(isCollapsed)
        return
      }
      const noFill =
        status === 'unfilled' || status === 'unfill-optimized' || (!status && (unanswered || blocked))
      if (noFill && !inView && belowViewport()) collapse(true)
    }

    // An ad blocker fails the script, or lets it load without the library, so
    // nothing is ever pushed and the slot will not fill. A script that is only
    // slow is neither, and keeps its reservation until AdSense answers.
    const markBlocked = () => {
      if (blocked || window.adsbygoogle) return
      blocked = true
      settle()
    }

    const watchScript = () => {
      if (script) return
      script = document.querySelector<HTMLScriptElement>(ADSENSE_SCRIPT_SELECTOR)
      if (!script) return
      // AdSenseLoader stamps the outcome, for a script that settled before
      // this band mounted.
      if (script.dataset.tlsState) {
        markBlocked()
        return
      }
      script.addEventListener('error', markBlocked)
      script.addEventListener('load', markBlocked)
    }

    const armClock = () => {
      if (timer || !band.querySelector('.ad-container[data-ad-pushed]')) return
      timer = setTimeout(() => {
        unanswered = true
        settle()
      }, UNANSWERED_TIMEOUT_MS)
    }

    const intersection = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting
      if (inView) everInView = true
      settle()
    })
    intersection.observe(band)

    const mutation = new MutationObserver(() => {
      armClock()
      settle()
    })
    mutation.observe(band, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-ad-status', 'data-ad-pushed']
    })
    armClock()

    // AdSenseLoader appends the script to <head> on the first interaction.
    const head = new MutationObserver(watchScript)
    head.observe(document.head, { childList: true })
    watchScript()

    return () => {
      intersection.disconnect()
      mutation.disconnect()
      head.disconnect()
      if (timer) clearTimeout(timer)
      if (scrollCheck) clearTimeout(scrollCheck)
      listenForScroll(false)
      script?.removeEventListener('error', markBlocked)
      script?.removeEventListener('load', markBlocked)
    }
  }, [])

  return (
    <div
      ref={bandRef}
      className={`${QUARANTINE_BAND} ${className}`}
      data-ad-collapsed={collapsed ? 'true' : undefined}
    >
      <div className={`tls-ad-caption ${captionClassName} text-center text-xs text-[var(--tls-fg-subtle)]`}>
        {caption}
      </div>
      {children}
    </div>
  )
}

/**
 * The full-width sponsored band, with quiet design-system styling and a
 * "Sponsored" label. Used on the homepage, sound detail, blog posts, /convert,
 * /guide and the category and model hubs.
 *
 * From `sm` it asks for "horizontal" and gets it (1280px serves 942x90, 90px
 * reserved). On a phone it is one fixed 320x100 unit with no format hint, so
 * the 100px reserved is the height that renders and a fill shifts nothing. A
 * slot that gets no ad collapses, but only while off screen (see AdBand).
 *
 * `InFeedAd` below uses the same slot ids and the same `data-ad-shape="band"`
 * reservation.
 */
export function HorizontalAd({
  slot,
  className = '',
  lazy = false
}: {
  slot: string
  className?: string
  lazy?: boolean
}) {
  const { marketingAllowed } = useCookieConsent()

  if (!marketingAllowed || !ADSENSE_CLIENT) {
    return null
  }

  return (
    <AdBand className={className} caption="Sponsored" captionClassName="mb-2">
      <AdPlacement slot={slot} format="horizontal" responsive lazy={lazy} />
    </AdBand>
  )
}

/**
 * In-feed ad for between content items. Quarantined the same as every other
 * slot — 1px hairline frame, quarantine ground, square corners — per §08.1.
 *
 * The frame matters more here than on the banded slot, not less. This one lands
 * in a list of rows, and the unit AdSense serves it at 375px is itself a list of
 * rows: a white panel headed "Discover more" with chevrons down the right edge
 * (logs/tes-2328/before-prod/viewport-375.png). Unframed, on a page whose entire
 * content model is a row, that is the failure §08.1 names outright — paid
 * content reading as product.
 */
export function InFeedAd({
  slot,
  className = '',
  lazy = true
}: {
  slot: string
  className?: string
  lazy?: boolean
}) {
  const { marketingAllowed } = useCookieConsent()

  if (!marketingAllowed || !ADSENSE_CLIENT) {
    return null
  }

  return (
    <AdBand className={`my-6 ${className}`} caption="Advertisement" captionClassName="mb-1">
      <AdPlacement slot={slot} format="horizontal" responsive lazy={lazy} />
    </AdBand>
  )
}

/**
 * Sidebar ad for vertical placements
 */
function SidebarAd({
  slot,
  className = ''
}: {
  slot: string
  className?: string
}) {
  const { marketingAllowed } = useCookieConsent()

  if (!marketingAllowed || !ADSENSE_CLIENT) {
    return null
  }

  return (
    <div className={`sticky top-24 ${className}`}>
      <div className="rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] p-4">
        <div className="mb-2 tls-eyebrow">Advertisement</div>
        <AdPlacement slot={slot} format="rectangle" responsive={false} />
      </div>
    </div>
  )
}

/**
 * Placeholder ad component for development/preview
 * Shows what an ad would look like without actually loading ads
 */
function AdPlaceholder({
  format = 'horizontal',
  className = ''
}: {
  format?: 'horizontal' | 'vertical' | 'rectangle'
  className?: string
}) {
  const sizes = {
    horizontal: 'h-24 w-full',
    vertical: 'w-40 h-[600px]',
    rectangle: 'w-[300px] h-[250px]'
  }

  return (
    <div className={`${sizes[format]} ${className}`}>
      <div className="flex size-full items-center justify-center rounded-[var(--ls-radius-0)] border border-dashed border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)]">
        <div className="text-center text-sm text-[var(--tls-fg-subtle)]">
          <div className="mb-1">Ad Space</div>
          <div className="text-xs">{format}</div>
        </div>
      </div>
    </div>
  )
}

// Add TypeScript declaration for window.adsbygoogle
declare global {
  interface Window {
    adsbygoogle: unknown[]
  }
}
